import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/hooks/use-toast';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarIcon, Search, X, Plus, TrendingUp } from 'lucide-react';
import { format, isValid } from 'date-fns';
import { ar, fr } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import type { Ingredient, Inventory, MeasurementUnit } from '@/types/entities';
import { useAddMultiReceipt } from '@/hooks/useInventoryReceipts';
import { MEASUREMENT_UNITS } from '@/lib/unit-utils';
import { IngredientIcon } from '@/components/ui/ingredient-icon';
import { useInventory } from '@/hooks/useInventory';

interface AddMultiIngredientReceiptDialogProps {
  restaurantId: string;
  ingredients: Ingredient[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface IngredientReceiptData {
  ingredientId: string;
  quantity: string;
  unit: MeasurementUnit;
  costPerUnit: string;
}

export function AddMultiIngredientReceiptDialog({
  restaurantId,
  ingredients,
  open,
  onOpenChange,
  onSuccess,
}: AddMultiIngredientReceiptDialogProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const locale = i18n.language === 'ar' ? ar : fr;
  const isRTL = i18n.language === 'ar';

  // Get inventory data to show current stock
  const { data: inventory } = useInventory(restaurantId);

  // Initialize with today's date at midnight
  const getTodayAtMidnight = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  };

  // Shared fields
  const [receiptDate, setReceiptDate] = useState<Date>(getTodayAtMidnight());
  const [supplier, setSupplier] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Selected ingredients with their receipt data
  const [selectedIngredients, setSelectedIngredients] = useState<
    Map<string, IngredientReceiptData>
  >(new Map());

  const { mutate: addMultiReceipt, isPending } = useAddMultiReceipt(restaurantId);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setReceiptDate(today);
      setSupplier('');
      setInvoiceNumber('');
      setNotes('');
      setSearchQuery('');
      setSelectedIngredients(new Map());
    }
  }, [open]);

  // Filter ingredients based on search query
  const filteredIngredients = useMemo(() => {
    if (!searchQuery.trim()) return ingredients;

    const query = searchQuery.toLowerCase();
    return ingredients.filter(
      (ing) => ing.nameAr.toLowerCase().includes(query) || ing.nameFr.toLowerCase().includes(query)
    );
  }, [ingredients, searchQuery]);

  // Get inventory for an ingredient
  const getInventoryForIngredient = (ingredientId: string): Inventory | undefined => {
    return inventory?.find((inv) => inv.ingredientId === ingredientId);
  };

  // Toggle ingredient selection
  const toggleIngredient = (ingredient: Ingredient) => {
    const newSelected = new Map(selectedIngredients);
    const inv = getInventoryForIngredient(ingredient.id);

    if (newSelected.has(ingredient.id)) {
      newSelected.delete(ingredient.id);
    } else {
      newSelected.set(ingredient.id, {
        ingredientId: ingredient.id,
        quantity: '',
        unit: (inv?.unit || 'kg') as MeasurementUnit,
        costPerUnit: '',
      });
    }
    setSelectedIngredients(newSelected);
  };

  // Update ingredient receipt data
  const updateIngredientData = (
    ingredientId: string,
    field: keyof IngredientReceiptData,
    value: string | MeasurementUnit
  ) => {
    const newSelected = new Map(selectedIngredients);
    const current = newSelected.get(ingredientId);
    if (current) {
      newSelected.set(ingredientId, {
        ...current,
        [field]: value,
      });
      setSelectedIngredients(newSelected);
    }
  };

  // Remove ingredient from selection
  const removeIngredient = (ingredientId: string) => {
    const newSelected = new Map(selectedIngredients);
    newSelected.delete(ingredientId);
    setSelectedIngredients(newSelected);
  };

  // Calculate total cost
  const totalCost = useMemo(() => {
    let total = 0;
    selectedIngredients.forEach((data) => {
      const qty = parseFloat(data.quantity) || 0;
      const cost = parseFloat(data.costPerUnit) || 0;
      total += qty * cost;
    });
    return total;
  }, [selectedIngredients]);

  // Validate form
  const validateForm = (): string | null => {
    if (selectedIngredients.size === 0) {
      return t('inventory.multiReceipt.selectAtLeastOne');
    }

    if (!supplier.trim()) {
      return t('inventory.multiReceipt.supplierRequired');
    }

    // Validate each selected ingredient
    for (const [ingredientId, data] of selectedIngredients.entries()) {
      const ingredient = ingredients.find((ing) => ing.id === ingredientId);
      // Fallback to ingredient ID if ingredient not found (shouldn't happen, but handle gracefully)
      const name = ingredient
        ? i18n.language === 'ar'
          ? ingredient.nameAr
          : ingredient.nameFr
        : ingredientId;

      if (!data.quantity || parseFloat(data.quantity) <= 0) {
        return t('inventory.multiReceipt.quantityRequired', { ingredient: name });
      }

      if (!data.costPerUnit || parseFloat(data.costPerUnit) <= 0) {
        return t('inventory.multiReceipt.costRequired', { ingredient: name });
      }
    }

    return null;
  };

  // Handle submit
  const handleSubmit = () => {
    const error = validateForm();
    if (error) {
      toast({
        title: t('common.error'),
        description: error,
        variant: 'destructive',
      });
      return;
    }

    // Build receipt items
    const receiptItems = Array.from(selectedIngredients.values()).map((data) => ({
      ingredientId: data.ingredientId,
      quantityReceived: parseFloat(data.quantity),
      unit: data.unit,
      costPerUnit: parseFloat(data.costPerUnit),
    }));

    const trimmedInvoice = invoiceNumber.trim();
    const trimmedNotes = notes.trim();

    addMultiReceipt(
      {
        receiptDate,
        supplier: supplier.trim(),
        ...(trimmedInvoice ? { invoiceNumber: trimmedInvoice } : {}),
        ...(trimmedNotes ? { notes: trimmedNotes } : {}),
        items: receiptItems,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
          onSuccess?.();
        },
      }
    );
  };

  const selectedIngredientsList = Array.from(selectedIngredients.keys())
    .map((id) => ingredients.find((ing) => ing.id === id))
    .filter((ing): ing is Ingredient => ing !== undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t('inventory.multiReceipt.title')}
          </DialogTitle>
          <DialogDescription>{t('inventory.multiReceipt.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 py-4">
          {/* Shared Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-b pb-4">
            {/* Receipt Date */}
            <div className="space-y-2">
              <Label>{t('inventory.addReceiptDialog.purchaseDate')}</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !receiptDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                    {receiptDate && isValid(receiptDate) ? (
                      format(receiptDate, 'PPP', { locale })
                    ) : (
                      <span>{t('inventory.addReceiptDialog.selectDate')}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align={isRTL ? 'end' : 'start'}>
                  <Calendar
                    mode="single"
                    selected={receiptDate}
                    onSelect={(date) => {
                      if (date) {
                        const localDate = new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate(),
                          0,
                          0,
                          0,
                          0
                        );
                        setReceiptDate(localDate);
                      }
                    }}
                    initialFocus
                    locale={locale}
                    dir={isRTL ? 'rtl' : 'ltr'}
                    weekStartsOn={isRTL ? 6 : 1}
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Supplier */}
            <div className="space-y-2">
              <Label htmlFor="supplier">{t('inventory.addReceiptDialog.supplier')} *</Label>
              <Input
                id="supplier"
                value={supplier}
                onChange={(e) => setSupplier(e.target.value)}
                placeholder={t('inventory.addReceiptDialog.supplierPlaceholder')}
                required
              />
            </div>

            {/* Invoice Number */}
            <div className="space-y-2">
              <Label htmlFor="invoice">{t('inventory.addReceiptDialog.invoiceNumber')}</Label>
              <Input
                id="invoice"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder={t('inventory.addReceiptDialog.invoicePlaceholder')}
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">{t('inventory.addReceiptDialog.notes')}</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('inventory.addReceiptDialog.notesPlaceholder')}
                rows={2}
              />
            </div>
          </div>

          {/* Ingredient Selection */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">
                {t('inventory.multiReceipt.selectIngredients')}
              </Label>
              <Badge variant="secondary">
                {selectedIngredients.size} {t('inventory.multiReceipt.selected')}
              </Badge>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('inventory.multiReceipt.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn('pl-9', isRTL && 'pr-9 pl-0')}
              />
            </div>

            {/* Selected Ingredients */}
            {selectedIngredientsList.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  {t('inventory.multiReceipt.selectedIngredients')}
                </Label>
                {selectedIngredientsList.map((ingredient) => {
                  const data = selectedIngredients.get(ingredient.id)!;
                  const qty = parseFloat(data.quantity) || 0;
                  const cost = parseFloat(data.costPerUnit) || 0;
                  const itemTotal = qty * cost;

                  return (
                    <Card key={ingredient.id} className="p-3">
                      <CardContent className="p-0 space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <IngredientIcon ingredient={ingredient} size={24} />
                            <span className="font-medium">
                              {i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeIngredient(ingredient.id)}
                            className="h-6 w-6"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <Label className="text-xs">
                              {t('inventory.addReceiptDialog.quantityReceived')} *
                            </Label>
                            <Input
                              type="number"
                              step="0.001"
                              min="0.001"
                              value={data.quantity}
                              onChange={(e) =>
                                updateIngredientData(ingredient.id, 'quantity', e.target.value)
                              }
                              placeholder="0.000"
                              className="h-8 text-sm"
                            />
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">
                              {t('inventory.addReceiptDialog.unit')} *
                            </Label>
                            <Select
                              value={data.unit}
                              onValueChange={(value) =>
                                updateIngredientData(
                                  ingredient.id,
                                  'unit',
                                  value as MeasurementUnit
                                )
                              }
                            >
                              <SelectTrigger className="h-8 text-sm">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {MEASUREMENT_UNITS.map((u) => (
                                  <SelectItem key={u} value={u}>
                                    {u}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-1">
                            <Label className="text-xs">
                              {t('inventory.addReceiptDialog.costPerUnit')} ({t('common.currency')})
                              *
                            </Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={data.costPerUnit}
                              onChange={(e) =>
                                updateIngredientData(ingredient.id, 'costPerUnit', e.target.value)
                              }
                              placeholder="0.00"
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>

                        {qty > 0 && cost > 0 && (
                          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-1.5">
                            {t('inventory.multiReceipt.itemTotal')}: {itemTotal.toFixed(2)}{' '}
                            {t('common.currency')}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Available Ingredients List */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t('inventory.multiReceipt.availableIngredients')}
              </Label>
              <div className="max-h-60 overflow-y-auto border rounded-md p-2 space-y-1">
                {filteredIngredients
                  .filter((ing) => !selectedIngredients.has(ing.id))
                  .map((ingredient) => {
                    const inv = getInventoryForIngredient(ingredient.id);
                    return (
                      <Button
                        key={ingredient.id}
                        variant="ghost"
                        className="w-full justify-start h-auto p-2"
                        onClick={() => toggleIngredient(ingredient)}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <IngredientIcon ingredient={ingredient} size={20} />
                          <span className="text-sm">
                            {i18n.language === 'ar' ? ingredient.nameAr : ingredient.nameFr}
                          </span>
                          {inv && (
                            <span className="text-xs text-muted-foreground ml-auto">
                              {inv.quantity.toFixed(3)} {inv.unit}
                            </span>
                          )}
                        </div>
                        <Plus className="h-4 w-4 ml-2" />
                      </Button>
                    );
                  })}
                {filteredIngredients.filter((ing) => !selectedIngredients.has(ing.id)).length ===
                  0 && (
                  <div className="text-center text-sm text-muted-foreground py-4">
                    {t('inventory.multiReceipt.noIngredientsAvailable')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Summary */}
          {selectedIngredients.size > 0 && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('inventory.multiReceipt.totalIngredients')}:
                </span>
                <span className="font-medium">{selectedIngredients.size}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {t('inventory.addReceiptDialog.totalCost')}:
                </span>
                <Badge variant="secondary" className="font-mono text-base">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {totalCost.toFixed(2)} {t('common.currency')}
                </Badge>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={isPending || selectedIngredients.size === 0 || !supplier.trim()}
          >
            {isPending
              ? t('inventory.multiReceipt.adding')
              : t('inventory.multiReceipt.addReceipt')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
